import { z } from 'zod';
import { paginationSchema } from './common.js';
// §7.6.23 -- CDPH SaPHIRE electronic lab reporting.
//
// Ports legacy Alpha's SAPHIRE_CONFIG validation (`validateSaphireConfig` in
// `Alpha-App/src/util/cdphSaphireConfig.js`). The mTLS PEM bundle itself is
// never round-tripped through these schemas -- it is write-only (see
// `updateCdphConfigSchema.certBundle`) and never appears in a response
// (see `cdphConfigResponseSchema`).
export const CDPH_ENDPOINTS = {
    stage: 'https://saphireapistage.cdph.ca.gov/hl7',
    prod: 'https://saphireapi.cdph.ca.gov/hl7',
};
export const cdphEnvironmentSchema = z.enum(['stage', 'prod']);
const cdphConfigFieldsSchema = z.object({
    enabled: z.boolean().default(false),
    environment: cdphEnvironmentSchema.default('stage'),
    endpointUrl: z.string().url().max(500).optional(),
    submitterOid: z.string().min(1).max(100).optional(),
    clia: z.string().min(1).max(20).optional(),
    sendingFacility: z.string().min(1).max(255).optional(),
});
// The enable-guard check itself: when `enabled` is true, the config fields
// required to build + submit an ORU^R01 must all be present, and a cert
// bundle must already be on file. The service layer is the source of truth
// for cert-on-file state (it never flows through the wire as key material),
// so callers pass that fact in via `certProvided` rather than the schema
// re-deriving it.
export const cdphConfigSchema = cdphConfigFieldsSchema
    .extend({
    certProvided: z.boolean().default(false),
})
    .superRefine((data, ctx) => {
    if (!data.enabled)
        return;
    const requiredFields = [
        'endpointUrl',
        'submitterOid',
        'clia',
        'sendingFacility',
    ];
    for (const field of requiredFields) {
        if (!data[field]) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [field],
                message: `${field} is required when CDPH reporting is enabled`,
            });
        }
    }
    if (!data.certProvided) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['certProvided'],
            message: 'A certificate bundle must be on file when CDPH reporting is enabled',
        });
    }
});
// PEM bundle for mTLS -- {key, cert, ca}, all-or-none.
const certBundleSchema = z
    .object({
    key: z.string().min(1).optional(),
    cert: z.string().min(1).optional(),
    ca: z.string().min(1).optional(),
})
    .superRefine((data, ctx) => {
    const provided = [data.key, data.cert, data.ca].filter((v) => v !== undefined).length;
    if (provided > 0 && provided < 3) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'certBundle requires key, cert, and ca together (all-or-none)',
        });
    }
});
// PUT /api/admin/cdph/config body. Structural validation only -- the
// enable-guard (which needs cert-on-file state) runs at the service layer
// via `cdphConfigSchema`.
export const updateCdphConfigSchema = cdphConfigFieldsSchema.extend({
    certBundle: certBundleSchema.optional(),
});
// Per-test SNOMED result-code overrides for OBX-5 (legacy `cdphConfig.
// snomedResultMap`, restored per legacy commit 6aad76d / MLF #62). Bounded so
// an admin can't paste an unbounded JSON blob into a jsonb column: at most 50
// entries, and each key/value capped well above any real SNOMED code/result
// label length. JSON-level config only in this pass -- no dedicated editor UI
// (PLAN.md §7.6.23).
const snomedResultMapSchema = z
    .record(z.string().max(100), z.string().max(100))
    .refine((map) => Object.keys(map).length <= 50, {
    message: 'snomedResultMap supports at most 50 entries',
});
export const testTypeCdphConfigSchema = z.object({
    isReportable: z.boolean(),
    reportPolarity: z.enum(['positive', 'all']).default('positive'),
    loinc: z.string().max(20).optional(),
    snomedResultMap: snomedResultMapSchema.optional(),
});
// GET /api/admin/cdph/config response shape -- sanitized (no key material).
// Ports legacy `sanitizeSaphireConfig`'s whitelist approach.
export const cdphConfigResponseSchema = z.object({
    enabled: z.boolean(),
    environment: cdphEnvironmentSchema,
    endpointUrl: z.string().nullable(),
    submitterOid: z.string().nullable(),
    clia: z.string().nullable(),
    sendingFacility: z.string().nullable(),
    certOnFile: z.boolean(),
    certUpdatedAt: z.string().nullable(),
});
// -----------------------------------------------------------------------
// Slice 4 -- report queue (GET/POST /api/admin/cdph/reports*).
// -----------------------------------------------------------------------
export const cdphReportStatusSchema = z.enum(['pending', 'sent', 'accepted', 'rejected', 'error', 'exhausted']);
// GET /api/admin/cdph/reports query params -- server-side filter + pagination
// (mirrors `auditQuerySchema`'s `.safeParse(request.query)` + coercion style,
// but pages via page/limit like `useApiList` expects, not offset).
export const cdphReportListQuerySchema = paginationSchema.extend({
    status: cdphReportStatusSchema.optional(),
});
const cdphReportErrDetailSchema = z.object({
    location: z.string().optional(),
    reason: z.string().optional(),
    severity: z.string().optional(),
});
// List row shape -- deliberately excludes `hl7Message` (PHI); see
// `cdphReportDetailSchema` for the full record used by the detail view.
export const cdphReportListItemSchema = z.object({
    id: z.string().uuid(),
    resultId: z.string().uuid(),
    orderId: z.string().uuid().nullable(),
    status: cdphReportStatusSchema,
    attempts: z.number().int(),
    ackCode: z.string().nullable(),
    ackText: z.string().nullable(),
    hl7MessageId: z.string().nullable(),
    sentAt: z.string().nullable(),
    nextAttemptAt: z.string().nullable(),
    createdAt: z.string(),
    // Joined from `results` for display -- the accession/barcode and test
    // type/code so an operator can recognize the row without a second lookup.
    barcode: z.string().nullable(),
    testType: z.string().nullable(),
});
// GET /api/admin/cdph/reports/:id -- full record incl. the stored HL7
// payload and structured ERR details, for the report-queue detail modal.
export const cdphReportDetailSchema = cdphReportListItemSchema.extend({
    organizationId: z.string().uuid(),
    hl7Message: z.string().nullable(),
    errDetails: z.array(cdphReportErrDetailSchema).nullable(),
    updatedAt: z.string(),
});
//# sourceMappingURL=cdph.js.map